import { makeAutoObservable } from "mobx";

import { IChatExchangeError } from "../models/interfaces/chat-exchange-error.interface";
import { IChatThreadExtendedModel } from "../models/interfaces/chat-thread-pending-model.interface";

export const THREAD_PAGE_SIZE = 20;

interface IThreadPagingEntry {
  thread: IChatThreadExtendedModel;
  pageIndex: number | undefined;
}

interface IThreadPagingState {
  threads: IThreadPagingEntry[];
  totalThreadCount: number;
  loadedThreadPageIndexes: Set<number>; // keeps track of which thread pages have been successfully loaded
  loadingThreadPageIndexes: Set<number>; // keeps track of which thread pages are currently being loaded
  failedThreadPageIndex: number | undefined; // the index of the thread page that failed to load
}

interface IActiveThreadState {
  activeThread: IChatThreadExtendedModel | undefined;
  threadErrors: IChatExchangeError[]; // stores errors related to the active thread
  nextActiveThreadLoadRequestId: number; // tracks active thread load requests (prevents stale async responses from being accepted after rapid thread switches)
}

export class ThreadStore {
  private _pagingState: IThreadPagingState = {
    threads: [],
    totalThreadCount: 0,
    loadedThreadPageIndexes: new Set<number>(),
    loadingThreadPageIndexes: new Set<number>(),
    failedThreadPageIndex: undefined,
  };

  private _activeThreadState: IActiveThreadState = {
    activeThread: undefined,
    threadErrors: [],
    nextActiveThreadLoadRequestId: 0,
  };

  public pendingCopyResponseToInput?: string;

  constructor() {
    makeAutoObservable(this);
  }

  private set threads(threads: IChatThreadExtendedModel[]) {
    const existingEntries = this._pagingState.threads;
    const nextEntries: IThreadPagingEntry[] = [];

    for (const thread of threads) {
      const existingEntry = existingEntries.find(entry => entry.thread.id === thread.id);
      nextEntries.push({
        thread,
        pageIndex: existingEntry?.pageIndex,
      });
    }

    this._pagingState.threads = nextEntries;
  }

  private getThreadEntry(threadId: string): IThreadPagingEntry | undefined {
    return this._pagingState.threads.find(entry => entry.thread.id === threadId);
  }

  private getThreadEntryIndex(threadId: string): number {
    return this._pagingState.threads.findIndex(entry => entry.thread.id === threadId);
  }

  public getThreadPageIndex(threadId: string): number | undefined {
    const entry = this.getThreadEntry(threadId);
    return entry?.pageIndex;
  }

  public get threads(): IChatThreadExtendedModel[] {
    return this._pagingState.threads.map(entry => entry.thread);
  }

  public get nextThreadPageIndex(): number {
    if (!this._pagingState.loadedThreadPageIndexes.size) {
      return 0;
    }

    return Math.max(...this._pagingState.loadedThreadPageIndexes) + 1;
  }

  public get loadedThreadPageIndexes(): number[] {
    return Array.from(this._pagingState.loadedThreadPageIndexes);
  }

  public get hasLoadedInitialThreadPage(): boolean {
    return this._pagingState.loadedThreadPageIndexes.has(0);
  }

  public get isLoadingMoreThreads(): boolean {
    return Array.from(this._pagingState.loadingThreadPageIndexes).some(pageIndex => pageIndex > 0);
  }

  public get failedThreadPageIndex(): number | undefined {
    return this._pagingState.failedThreadPageIndex;
  }

  public get hasThreadLoadError(): boolean {
    return this._pagingState.failedThreadPageIndex !== undefined;
  }

  public get hasMoreThreads(): boolean {
    if (!this.hasLoadedInitialThreadPage || this.hasThreadLoadError) {
      return false;
    }

    // Uses page boundary instead of unique thread count
    // makes pagination terminate deterministically and prevents the endless page-load loop after cross-tab updates and reorders (the loaded unique count can temporarily drift and keep triggering endless lazy loads)
    return this.nextThreadPageIndex * THREAD_PAGE_SIZE < this._pagingState.totalThreadCount;
  }

  public get activeThread(): IChatThreadExtendedModel | undefined {
    return this._activeThreadState.activeThread;
  }

  public get isLoadingActiveThreadDetails(): boolean {
    return this._activeThreadState.activeThread?.loadState === "loading";
  }

  public get hasActiveThreadLoadError(): boolean {
    return this._activeThreadState.activeThread?.loadState === "error";
  }

  public get threadErrors(): IChatExchangeError[] {
    return this._activeThreadState.threadErrors;
  }

  public set threadErrors(errors: IChatExchangeError[]) {
    this._activeThreadState.threadErrors = errors;
  }

  public triggerCopyResponseToInput(input: string | undefined): void {
    this.pendingCopyResponseToInput = input;
  }

  public startLoadingThreadPage(pageIndex: number, forceReload = false): boolean {
    if (this._pagingState.loadingThreadPageIndexes.has(pageIndex)) {
      return false;
    }

    if (!forceReload && this._pagingState.loadedThreadPageIndexes.has(pageIndex)) {
      return false;
    }

    this._pagingState.loadingThreadPageIndexes.add(pageIndex);

    this.clearFailedThreadLoadErrorForPage(pageIndex);

    return true;
  }

  public finishLoadingThreadPage(pageIndex: number): void {
    this._pagingState.loadingThreadPageIndexes.delete(pageIndex);
  }

  public setThreadLoadError(pageIndex: number): void {
    this._pagingState.failedThreadPageIndex = pageIndex;
  }

  public mergeThreadPage(pageIndex: number, updatedThreads: IChatThreadExtendedModel[], totalCount: number): void {
    const previousEntries = this._pagingState.threads;
    const updatedThreadIds = new Set(updatedThreads.map(thread => thread.id));
    const retainedEntries = previousEntries.filter(entry => {
      return entry.pageIndex !== pageIndex && !updatedThreadIds.has(entry.thread.id);
    });
    const insertAt = previousEntries.findIndex(entry => entry.pageIndex === pageIndex);
    const updatedEntries: IThreadPagingEntry[] = [];

    for (const updatedThread of updatedThreads) {
      const existingEntry = previousEntries.find(entry => entry.thread.id === updatedThread.id);
      const mergedThread = existingEntry ? this.mergeThreadState(updatedThread, existingEntry.thread) : updatedThread;
      updatedEntries.push({ thread: mergedThread, pageIndex });
    }

    if (insertAt === -1) {
      this._pagingState.threads = [...retainedEntries, ...updatedEntries];
    } else {
      this._pagingState.threads = [
        ...retainedEntries.slice(0, insertAt),
        ...updatedEntries,
        ...retainedEntries.slice(insertAt),
      ];
    }

    this._pagingState.totalThreadCount = Math.max(totalCount, this._pagingState.threads.length);
    this._pagingState.loadedThreadPageIndexes.add(pageIndex);

    this.clearFailedThreadLoadErrorForPage(pageIndex);
  }

  // Marks the requested thread as loading and dedupes duplicate loadFromApi while the same request is in progress
  public beginOpeningThread(thread: IChatThreadExtendedModel): { requestId: number; shouldLoadFromApi: boolean } {
    const activeThread = this._activeThreadState.activeThread;

    if (activeThread?.id === thread.id && activeThread.loadState === "loading") {
      return { requestId: activeThread.activeThreadLoadRequestId, shouldLoadFromApi: false };
    }

    const requestId = this.getNextActiveThreadLoadRequestId();
    this._activeThreadState.activeThread = {
      ...thread,
      loadState: "loading",
      activeThreadLoadRequestId: requestId,
    };
    return { requestId, shouldLoadFromApi: true };
  }

  public completeActiveThreadLoad(requestId: number, thread: IChatThreadExtendedModel): void {
    const activeThread = this._activeThreadState.activeThread;
    if (requestId !== activeThread?.activeThreadLoadRequestId || activeThread?.id !== thread.id) {
      return;
    }

    const loadedThread: IChatThreadExtendedModel = {
      ...thread,
      loadState: "loaded",
      activeThreadLoadRequestId: requestId,
    };
    const existingEntryIndex = this.getThreadEntryIndex(thread.id);

    if (existingEntryIndex !== -1) {
      const existingPageIndex = this._pagingState.threads[existingEntryIndex].pageIndex;
      this._pagingState.threads[existingEntryIndex] = {
        thread: loadedThread,
        pageIndex: existingPageIndex,
      };
    } else {
      this._pagingState.threads.push({ thread: loadedThread, pageIndex: undefined });
    }

    this._activeThreadState.activeThread = loadedThread;
  }

  public failActiveThreadLoad(requestId: number, threadId: string): void {
    const activeThread = this._activeThreadState.activeThread;
    if (requestId !== activeThread?.activeThreadLoadRequestId || activeThread?.id !== threadId) {
      return;
    }

    this._activeThreadState.activeThread = {
      ...activeThread,
      loadState: "error",
      activeThreadLoadRequestId: requestId,
    };
  }

  public setActiveThread(thread: IChatThreadExtendedModel): void {
    this._activeThreadState.activeThread = {
      ...thread,
      loadState: "loaded",
      activeThreadLoadRequestId: this.getNextActiveThreadLoadRequestId(),
    };

    const existingEntryIndex = this.getThreadEntryIndex(thread.id);
    if (existingEntryIndex !== -1) {
      this._pagingState.threads[existingEntryIndex] = {
        thread,
        pageIndex: this._pagingState.threads[existingEntryIndex].pageIndex,
      };
    }
  }

  public resetActiveThread(): void {
    this._activeThreadState.activeThread = undefined;
  }

  // NOTE #63781 Called after the BE response is received to persist the final thread state.
  // Updates the activeThread if it matches, then either updates the existing thread or,
  // for a new thread that does not exist yet, adds it in the thread list.
  // A missing thread is expected here (new thread), so it is not treated as an error.
  public updateOrCreatePendingThread(updatedPendingThread: IChatThreadExtendedModel): void {
    if (this._activeThreadState.activeThread?.id === updatedPendingThread.id) {
      this._activeThreadState.activeThread = {
        ...updatedPendingThread,
        loadState: "loaded",
        activeThreadLoadRequestId: this.getNextActiveThreadLoadRequestId(),
      };
    }

    const existingEntryIndex = this.getThreadEntryIndex(updatedPendingThread.id);

    if (existingEntryIndex !== -1) {
      this._pagingState.threads[existingEntryIndex] = {
        thread: updatedPendingThread,
        pageIndex: this._pagingState.threads[existingEntryIndex].pageIndex,
      };
    } else if (updatedPendingThread.id) {
      this._pagingState.threads.push({
        thread: updatedPendingThread,
        pageIndex: undefined,
      });

      if (this.hasLoadedInitialThreadPage) {
        this._pagingState.totalThreadCount += 1;
      }
    }
  }

  // NOTE #63781 Called before the BE response to reflect a submitted prompt in a pending thread.
  // Sets the activeThread (when it matches threadId or when the request comes from a new thread) and
  // updates pendingPrompt on the existing thread so the pending state shows immediately.
  // The final state is written later by updateOrCreatePendingThread once the BE responds.
  public setPendingPromptInThread(threadId: string | undefined, pendingPrompt: string | undefined): void {
    const activeThread = this._activeThreadState.activeThread;

    // Creates a new active thread to reflect the pending state immediately
    if (!activeThread && !threadId && pendingPrompt) {
      this._activeThreadState.activeThread = {
        pendingPrompt,
        loadState: "loaded",
        id: "",
        threadSummary: "",
        visibleToUser: true,
        lastActivity: new Date(),
        userId: "",
        exchanges: [],
        groundingFileInfos: [],
        activeThreadLoadRequestId: this.getNextActiveThreadLoadRequestId(),
      };
      return;
    }

    if (!threadId) {
      return;
    }

    // Updates the pendingPrompt of the active thread
    if (activeThread && activeThread.id === threadId) {
      this._activeThreadState.activeThread = {
        ...activeThread,
        pendingPrompt,
        loadState: activeThread.loadState,
        activeThreadLoadRequestId: activeThread.activeThreadLoadRequestId,
      };
    }

    // Updates the pendingPrompt of the existing thread in the paging state if it exists
    const existingThreadEntryIndex = this.getThreadEntryIndex(threadId);
    if (existingThreadEntryIndex !== -1) {
      const existingThreadEntry = this._pagingState.threads[existingThreadEntryIndex];
      this._pagingState.threads[existingThreadEntryIndex] = {
        thread: { ...existingThreadEntry.thread, pendingPrompt },
        pageIndex: existingThreadEntry.pageIndex,
      };
    }
  }

  public removeThread(threadId: string): void {
    const removedThreadIndex = this.getThreadEntryIndex(threadId);
    const hadThread = removedThreadIndex !== -1;

    if (hadThread) {
      this._pagingState.threads.splice(removedThreadIndex, 1);
    }

    if (hadThread && this.hasLoadedInitialThreadPage) {
      this._pagingState.totalThreadCount = Math.max(0, this._pagingState.totalThreadCount - 1);

      if (this._pagingState.loadedThreadPageIndexes.size > 0) {
        const lastLoadedPageIndex = Math.max(...this._pagingState.loadedThreadPageIndexes);
        this._pagingState.loadedThreadPageIndexes.delete(lastLoadedPageIndex);
      }

      this._pagingState.failedThreadPageIndex = undefined;
    }

    if (this._activeThreadState.activeThread?.id === threadId) {
      this.resetActiveThread();
    }
  }

  private mergeThreadState(
    thread: IChatThreadExtendedModel,
    localState: IChatThreadExtendedModel,
  ): IChatThreadExtendedModel {
    return {
      ...thread,
      threadSummary: localState.threadSummary,
      visibleToUser: localState.visibleToUser,
      lastActivity: localState.lastActivity,
      userId: localState.userId,
      exchanges: thread.exchanges ?? localState.exchanges,
      pendingPrompt: localState.pendingPrompt ?? thread.pendingPrompt,
      loadState: localState.loadState ?? thread.loadState,
      activeThreadLoadRequestId: localState.activeThreadLoadRequestId ?? thread.activeThreadLoadRequestId,
    };
  }

  private getNextActiveThreadLoadRequestId(): number {
    this._activeThreadState.nextActiveThreadLoadRequestId += 1;
    return this._activeThreadState.nextActiveThreadLoadRequestId;
  }

  private clearFailedThreadLoadErrorForPage(pageIndex: number): void {
    if (this._pagingState.failedThreadPageIndex === pageIndex) {
      this._pagingState.failedThreadPageIndex = undefined;
    }
  }
}
